/* eslint-disable indent */
import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { useEffect } from 'react';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { useListAllEquipments } from '@/hooks/equipment.hooks';
import { Checkbox } from '@/components/Controls/Checkbox';
import { getTranslatedItem } from '@/utils/language.utils';
import { SmallCard } from '@/components/Custom/Card/SmallCard';
import { TEquipmentDetectionSchema } from '@/schemas/equipment-detection/equipment-detection.schema';
import { TitleSection } from '@/components/Header/Section/TitleSection';

interface IOppositeField {
  items: TEquipmentDetectionSchema[];
  name: string;
}
interface IOnCheckEquipment {
  item: TEquipmentDetectionSchema;
  currentItems: TEquipmentDetectionSchema[];
  currentFieldName: string;
  oppositeFields: IOppositeField[];
}

export const EquipmentWhitelistBlacklistManagement = <
  TSchema extends FieldValues
>({
  t,
  data,
  setValue,
  watch,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const { data: equipments, isLoading: isLoadingEquipments } =
    useListAllEquipments();

  const translatedEquipment = getTranslatedItem(data?.translatedEquipments);

  /**
   * === Set Value Watchers section ===
   */
  const whitelistedEquipmentsValue =
    watch &&
    watch(
      'whitelistedEquipments' as Path<TSchema>,
      [] as PathValue<TSchema, Path<TSchema>>
    );

  const blacklistedEquipmentsValue =
    watch &&
    watch(
      'blacklistedEquipments' as Path<TSchema>,
      [] as PathValue<TSchema, Path<TSchema>>
    );
  /**
   * === END OF Set Value Watchers section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue('name' as Path<TSchema>, data.name);

      setValue(
        'whitelistedEquipments' as Path<TSchema>,
        data.whitelistedEquipments ?? null
      );
      setValue(
        'blacklistedEquipments' as Path<TSchema>,
        data.blacklistedEquipments ?? null
      );
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  const onCheckEquipment = ({
    item,
    currentItems,
    currentFieldName,
    oppositeFields,
  }: IOnCheckEquipment) => {
    let itemsToUpdate: TEquipmentDetectionSchema[];

    const itemExists = currentItems?.find(
      (equipment) => equipment?.equipmentId === item?.equipmentId
    );

    if (itemExists) {
      // If item is already checked, remove it from the current list
      itemsToUpdate = currentItems.filter(
        (equipment) => equipment?.equipmentId !== item?.equipmentId
      );
    } else {
      // If not checked, add the item to the current list
      itemsToUpdate = [...(currentItems || []), item];

      // Remove the item from opposite fields
      if (oppositeFields?.length > 0) {
        for (const field of oppositeFields) {
          if (field?.items?.length > 0) {
            const hasItemInOppositeField = field?.items?.some(
              (equipment) => equipment?.equipmentId === item?.equipmentId
            );

            if (hasItemInOppositeField) {
              const updatedOppositeItems = field?.items?.filter(
                (equipment) => equipment?.equipmentId !== item?.equipmentId
              );

              setValue(
                field?.name as Path<TSchema>,
                updatedOppositeItems as PathValue<TSchema, Path<TSchema>>
              );
            }
          }
        }
      }
    }

    // Update the current field
    setValue(
      currentFieldName as Path<TSchema>,
      itemsToUpdate as PathValue<TSchema, Path<TSchema>>
    );
  };

  const renderEquipments = () => {
    if (!isLoadingEquipments && equipments?.data?.length > 0) {
      return equipments?.data?.map((equipment) => {
        const { id, name } = equipment;

        const translatedEquipment = getTranslatedItem(
          equipment?.translatedEquipments
        );

        const checkedWhiteListedEquipmentId = whitelistedEquipmentsValue?.find(
          (equipment) => equipment?.equipmentId === id
        )?.equipmentId;

        const checkedBlacklistedEquipmentId = blacklistedEquipmentsValue?.find(
          (equipment) => equipment?.equipmentId === id
        )?.equipmentId;

        return (
          <div className="flex gap-1" key={equipment?.id}>
            <div className="flex gap-3">
              <Checkbox
                checkboxClassName="!border-purple-5433FF/40"
                name={`${equipment?.id}-whiteListed`}
                checked={checkedWhiteListedEquipmentId}
                onChange={() =>
                  onCheckEquipment({
                    item: {
                      equipmentId: id,
                      classId: equipment?.classId,
                    },
                    currentItems: whitelistedEquipmentsValue || [],
                    currentFieldName: 'whitelistedEquipments',
                    oppositeFields: [
                      {
                        items: blacklistedEquipmentsValue || [],
                        name: 'blacklistedEquipments',
                      },
                    ],
                  })
                }
              />
              <Checkbox
                checkboxClassName="!border-red-FF2929/50 checked:!bg-red-FF2929"
                name={`${equipment?.id}-blackListed`}
                checked={checkedBlacklistedEquipmentId}
                onChange={() =>
                  onCheckEquipment({
                    item: {
                      equipmentId: id,
                      classId: equipment?.classId,
                    },
                    currentItems: blacklistedEquipmentsValue || [],
                    currentFieldName: 'blacklistedEquipments',
                    oppositeFields: [
                      {
                        items: whitelistedEquipmentsValue || [],
                        name: 'whitelistedEquipments',
                      },
                    ],
                  })
                }
              />
              <div className="font-normal text-[13.5px]">
                {translatedEquipment?.name || name}
              </div>
            </div>
          </div>
        );
      });
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <Card
        title={
          <TitleSection
            text={t('whitelistedAndBlacklistedEquipments')}
            defaultIconClassName="ki-questionnaire-tablet"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={t('common:save')}
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          <div className="flex gap-4 mb-2">
            <SmallCard
              text={t('whitelistedEquipments')}
              textLeftIcon={
                <i className="-mt-0.5 ki-outline ki-plus-circle text-primary text-xl" />
              }
              description={
                <div>
                  <div>{t('equipmentsAllowedToBeDetectedAbove')} :</div>
                  <div className="font-medium">
                    {translatedEquipment?.name || data?.name}
                  </div>
                </div>
              }
            />
            <SmallCard
              text={t('blacklistEquipments')}
              textLeftIcon={
                <i className="-mt-0.5 ki-solid ki-minus-circle text-red-FF2929 text-xl" />
              }
              description={
                <div>
                  <div>{t('equipmentsNotAllowedToBeDetectedAbove')} :</div>
                  <div className="font-medium">
                    {translatedEquipment?.name || data?.name}
                  </div>
                </div>
              }
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex gap-x-[14px]">
                <i className="ki-outline ki-plus-circle text-primary self-center text-lg" />
                <i className="ki-solid ki-minus-circle text-red-FF2929 self-center text-lg" />
              </div>
            ))}
            {renderEquipments()}
          </div>
        </>
      </Card>
    </form>
  );
};
